import { RouteState } from '@circuitry-ai/doc-data';
import { AuditLogsAgGridWrapper, ANALYST_AUDIT_LOG_STATIC_FILTERS, CustomTabs, PageHeader } from '@circuitry-ai/doc-ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnalystTabs } from './analystBoard';
import AnalyticsList from './analyticsList';
import { Key, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import AnalystHistory from './analysthistory';

/** A tab the app adds to the Analysts page, after the built-in ones (e.g. Analyst Agent). */
export interface AnalystPageTab {
  readonly key: string;
  /** Translated by the page. */
  readonly title: string;
  readonly endContent?: ReactNode;
  /** The tab fills the page and scrolls inside itself, instead of the page scrolling. */
  readonly fillHeight?: boolean;
  readonly render: () => ReactNode;
}

/* eslint-disable-next-line */
export interface AnalystpageProps {
  readonly extraTabs?: readonly AnalystPageTab[];
}

enum AnalystPageTabs {
  ANALYST_LIST = 'list',
  ANALYST_HISTORY = 'analyst-history',
  AUDIT_LOGS = 'audit-logs',
}

const NO_EXTRA_TABS: readonly AnalystPageTab[] = [];

export function Analystpage({ extraTabs = NO_EXTRA_TABS }: AnalystpageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const analystPageTabs = [
    { key: AnalystPageTabs.ANALYST_LIST, title: t('All Analysts'), id: 'all-analyst-tab' },
    { key: AnalystPageTabs.ANALYST_HISTORY, title: t('Analyst History'), id: 'analyst-history-tab' },
    { key: AnalystPageTabs.AUDIT_LOGS, title: t('Audit Logs'), id: 'analyst-audit-logs-tab' },
    ...extraTabs.map((tab) => ({ key: tab.key, title: t(tab.title), id: `${tab.key}-tab`, endContent: tab.endContent })),
  ];

  // The tab lives in ?tab=, so links and the back button can open any tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get('tab') ?? '';
  const currentTab = analystPageTabs.some((tab) => tab.key === tabValue) ? tabValue : AnalystPageTabs.ANALYST_LIST;
  const extraTab = extraTabs.find((tab) => tab.key === currentTab);

  const onTabChange = (tab: Key) => {
    // A tab's own parameters (e.g. the Analyst Agent's section) do not carry over to another tab.
    setSearchParams({ tab: String(tab) });
  };

  const renderSelectedTab = () => {
    switch (currentTab) {
      case AnalystPageTabs.ANALYST_LIST:
        return <AnalyticsList />;
      case AnalystPageTabs.ANALYST_HISTORY:
        return <AnalystHistory />;
      case AnalystPageTabs.AUDIT_LOGS:
        return (
            <AuditLogsAgGridWrapper staticFilters={ANALYST_AUDIT_LOG_STATIC_FILTERS} />
        );
      default:
        return null;
    }
  };

  return (
    <div className='flex flex-col h-full min-h-0'>
      <PageHeader
        heading="AInalysts"
        description={'View all your AInalysts reports at one place.'}
        showCreateButton={currentTab === AnalystPageTabs.ANALYST_LIST}
        createButtonText={'Create Analyst'}
        entityType="analyst"
        onCreateButtonClick={() => navigate(`/analytics/${RouteState.CREATE}?tab=${AnalystTabs.CONFIGURATION}`)}
      />
      <div className="advisor-tab-list mb-3">
        <CustomTabs
          tabs={analystPageTabs}
          selectedKey={currentTab}
          onTabChange={onTabChange}
        />
      </div>
      {extraTab?.fillHeight ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{extraTab.render()}</div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">{extraTab ? extraTab.render() : renderSelectedTab()}</div>
      )}
    </div>
  );
}

export default Analystpage;
